import { useEffect, useRef, useState } from "react";
import {
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  type QuotationDetailDto,
  type QuotationCreateInput,
} from "@hisense/shared";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import {
  useQuotation,
  useCreateQuotation,
  useUpdateQuotation,
  useQuotationCustomerOptions,
  useCustomer,
  useSettings,
} from "@/api/hooks";
import { PageBody, PageHeader } from "@/components/ui/PageHeader";
import { PrintLanguageToggle } from "@/components/docs/PrintLanguageToggle";
import { usePageCrumbs } from "@/components/shell/breadcrumb";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { SearchableFilter } from "@/components/ui/SearchableFilter";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { LoadingState, ErrorState } from "@/components/ui/States";
import {
  QuotationLinesEditor,
  lineFromDto,
  quotationDraftsToInput,
  type QuotationLineDraft,
} from "@/components/quotations/QuotationLinesEditor";
import { formatDate } from "@/domain/datetime";
import { FieldGrid, FormGroup, Hint, SPAN_CLASS } from "@/components/ui/FormLayout";
import { quotationSideColumnsUsed, type QuotationHeaderDraft } from "@/components/quotations/quotationFormLayout";
import { getLocalizedError } from "@/lib/api";
import { contactOptionLabel } from "@/components/customers/contactOption";
const dateInput = (value: number | null | undefined) =>
  value ? formatDate(value) : "";
const dateValue = (value: string, end = false) =>
  value
    ? new Date(value + (end ? "T23:59:59.999" : "T00:00:00")).getTime()
    : null;
export function QuotationEditorPage() {
  const { id } = useParams();
  const result = useQuotation(id);
  const { t } = useTranslation();
  if (id && result.isLoading)
    return (
      <PageBody>
        <LoadingState />
      </PageBody>
    );
  if (id && (result.error || !result.data))
    return (
      <PageBody>
        <ErrorState
          message={t("quotations.errorLoad")}
          onRetry={() => void result.refetch()}
        />
      </PageBody>
    );
  return <QuotationEditor key={id ?? "new"} existing={result.data} />;
}
function QuotationEditor({ existing }: { existing?: QuotationDetailDto }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const { search } = useLocation();
  const context = search;
  const base = useRef(existing);
  const create = useCreateQuotation();
  const update = useUpdateQuotation(existing?.id ?? "new");
  const busy = create.isPending || update.isPending;
  const request = useRef(crypto.randomUUID());
  const lock = useRef(false);
  const allow = useRef(false);
  const [h, setH] = useState<QuotationHeaderDraft>({
    customerId: existing?.customerId ?? "",
    contactId: existing?.contactId ?? "",
    omitCustomer: existing ? !existing.customerId : false,
    attn: existing?.attn ?? "",
    preparedBy: existing
      ? (existing.preparedBy ?? "")
      : (user?.displayName ?? ""),
    currency: existing?.currency ?? "USD",
    remarks: existing?.remarks ?? "",
    issueDate: existing ? dateInput(existing.issueDate) : dateInput(Date.now()),
    printingDate: dateInput(existing?.printingDate),
    validUntil: dateInput(existing?.validUntil),
    validityTerms: existing
      ? (existing.validityTerms ?? "")
      : t("quotationWorkspace.defaultValidity"),
    printLanguage: existing?.printLanguage ?? "en",
  });
  /**
   * Báo giá mới mở ra với KHÔNG dòng nào.
   *
   * Dòng trắng dựng sẵn khiến khách tưởng là lỗi: nó không có mã, không có mô
   * tả, không in ra, và thêm sản phẩm thì lại nhảy xuống dòng 2. Đã có nút
   * "Thêm dòng" cho ai muốn gõ tay.
   */
  const [lines, setLines] = useState<QuotationLineDraft[]>(
    existing?.items.map(lineFromDto) ?? [],
  );
  const [dirty, setDirty] = useState(false);
  const [refreshRecipient, setRefreshRecipient] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currencyReview, setCurrencyReview] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  /** "Hiện cột phụ" — có dòng dùng cột phụ thì cột phụ luôn hiện (quotationSideColumnsUsed). */
  const [sideOpen, setSideOpen] = useState(false);
  const customerList = useQuotationCustomerOptions();
  const customer = useCustomer(h.customerId || undefined);
  const settings = useSettings();
  const lists = settings.data?.businessOperations.optionLists;
  const currencies = lists?.currencies?.length ? lists.currencies : ["USD", "HKD", "RMB", "VND"];
  const options = (customerList.data ?? []).map((c) => ({
    value: c.id,
    label: c.companyName,
    sublabel: c.address,
  }));
  if (h.customerId && !options.some((c) => c.value === h.customerId))
    options.push({
      value: h.customerId,
      label:
        existing?.customerName ?? t("quotationWorkspace.unavailableCustomer"),
      sublabel: null,
    });
  const canWrite =
    !!user &&
    can(existing ? "records:update" : "records:create") &&
    existing?.status !== "Issued" &&
    !existing?.deletedAt;
  const change = (patch: Partial<typeof h>) => {
    setH((prev) => ({ ...prev, ...patch }));
    setDirty(true);
    setError(null);
  };
  const changeCurrency = (currency: string) => {
    const next = currency.trim().toUpperCase();
    if (next === h.currency) return;
    setH((prev) => ({ ...prev, currency: next }));
    setLines((prev) =>
      prev.map((l) => ({
        ...l,
        unitPrice: "",
        exwPrice: "",
        dtdPrice: "",
        underMoqPrice: "",
      })),
    );
    setCurrencyReview(true);
    setReviewed(false);
    setDirty(true);
    setError(null);
  };
  const blocker = useBlocker(() => !allow.current && (dirty || lock.current));
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!allow.current && (dirty || lock.current)) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);
  usePageCrumbs(
    existing
      ? [
          {
            label: existing.quotationNo,
            to: `/quotations/${existing.id}${context}`,
          },
          { label: t("common.edit") },
        ]
      : [{ label: t("quotations.new") }],
    `/quotations${context}`,
  );
  const save = async () => {
    if (lock.current || !canWrite) return;
    setError(null);
    const conv = quotationDraftsToInput(lines);
    if ("error" in conv) {
      setError(
        t("common.lineError", {
          line: conv.error.line,
          field: t(`common.${conv.error.field}`),
        }),
      );
      return;
    }
    if (!conv.items.length) {
      setError(t("quotationWorkspace.linesRequired"));
      return;
    }
    if (!h.customerId && !h.omitCustomer) {
      setError(t("quotationWorkspace.customerRequired"));
      return;
    }
    if (!h.currency || !h.issueDate) {
      setError(t("quotationWorkspace.requiredHeader"));
      return;
    }
    if (currencyReview && !reviewed) {
      setError(t("quotationWorkspace.reviewCurrency"));
      return;
    }
    const payload: QuotationCreateInput = {
      requestId: request.current,
      customerId: h.omitCustomer ? "" : h.customerId,
      omitCustomer: h.omitCustomer,
      contactId: h.omitCustomer ? null : h.contactId || null,
      attn: h.attn || null,
      preparedBy: h.preparedBy || null,
      currency: h.currency,
      remarks: h.remarks || null,
      issueDate: dateValue(h.issueDate),
      printingDate: dateValue(h.printingDate),
      validUntil: dateValue(h.validUntil, true),
      validityTerms: h.validityTerms || null,
      printLanguage: h.printLanguage,
      items: conv.items,
      expectedUpdatedAt: base.current?.updatedAt,
      refreshRecipient,
      pricesReviewed: currencyReview && reviewed,
    };
    try {
      lock.current = true;
      const r = existing
        ? await update.mutateAsync(payload)
        : await create.mutateAsync(payload);
      allow.current = true;
      setDirty(false);
      navigate(`/quotations/${r.data.id}${context}`, { replace: true });
    } catch (e) {
      setError(getLocalizedError(e, t, "quotations.errorSave"));
    } finally {
      lock.current = false;
    }
  };
  const sideUsed = quotationSideColumnsUsed(lines);
  const showSide = sideOpen || sideUsed;
  const cancelTo = existing ? `/quotations/${existing.id}${context}` : `/quotations${context}`;
  // Khối "In trên báo giá": người nhận đã chốt trên báo giá (hoặc đang lấy từ khách hàng vừa chọn), và nút cập nhật người nhận.
  const showRecipient = Boolean(customer.data || existing?.printCustomer) && !h.omitCustomer;
  const showRefresh = Boolean(existing && h.customerId && !h.omitCustomer);
  const contact = customer.data?.contacts.find((c) => c.id === h.contactId);
  const recipientLines =
    existing && !refreshRecipient && h.customerId === existing.customerId && h.contactId === (existing.contactId ?? "")
      ? [existing.printCustomer?.companyName, existing.printCustomer?.address, existing.printCustomer?.phone, existing.printCustomer?.email]
      : [customer.data?.companyName, customer.data?.address, contact?.phone1, contact?.email];

  /*
   * Bố cục (19/09/2026, cùng chuẩn PI và đơn hàng mẫu — quotationFormLayout.ts): tiêu đề + ngôn ngữ bản in ở đầu trang (không còn
   * nút Hủy / Lưu ở đó); Khách hàng; Điều khoản; Hàng báo giá (bảng độ rộng cố định, cột phụ ẩn khi không dùng); Ghi chú & hiệu
   * lực; một thanh Hủy / Lưu dính đáy.
   */
  return (
    <PageBody className="space-y-4">
      <PageHeader
        title={existing ? t("quotationWorkspace.editTitle") : t("quotations.new")}
        subtitle={existing ? [existing.quotationNo, existing.customerName].filter(Boolean).join(" · ") : t("quotationWorkspace.autoNumberHint")}
        actions={
          <PrintLanguageToggle
            value={h.printLanguage}
            onChange={(lang) => { setDirty(true); setH((v) => ({ ...v, printLanguage: lang })); }}
            disabled={busy || !canWrite}
          />
        }
      />
      {!canWrite && (
        <p className="rounded-sm bg-sunken px-3 py-2 text-sm text-warn">
          {t("quotationWorkspace.quotation_issued_locked")}
        </p>
      )}
      <fieldset disabled={!canWrite || busy} className="min-w-0 space-y-4">
        <FormGroup id="quote-group-customer" title={t("quotationWorkspace.groups.customer")} hint={t("quotationWorkspace.groups.customerHint")}>
          <FieldGrid>
            <div className={SPAN_CLASS[6]}>
              <FieldLabel>{t("pi.billTo")}</FieldLabel>
              <SearchableFilter
                className="h-9 w-full"
                label={t("pi.billTo")}
                allLabel={t("quotationWorkspace.chooseCustomer")}
                value={h.customerId}
                options={options}
                onChange={(value) => change({ customerId: value, contactId: "", attn: "", omitCustomer: false })}
                isLoading={customerList.isLoading}
                error={customerList.error ? t("quotationWorkspace.customerLoadError") : null}
                onRetry={() => void customerList.refetch()}
              />
              <label className="mt-2 flex items-center gap-2 text-xs text-ink-soft">
                <input
                  type="checkbox"
                  checked={h.omitCustomer}
                  onChange={(e) =>
                    change({ omitCustomer: e.target.checked, customerId: e.target.checked ? "" : h.customerId, contactId: "", attn: "" })
                  }
                  className="size-3.5 accent-[color:var(--color-brand)]"
                />
                {t("quotationWorkspace.generalPriceList")}
              </label>
            </div>
            <div className={SPAN_CLASS[3]}>
              <FieldLabel htmlFor="quote-contact">{t("quotationWorkspace.contact")}</FieldLabel>
              <Select
                id="quote-contact"
                containerClassName="w-full"
                aria-label={t("quotationWorkspace.contact")}
                value={h.contactId}
                onChange={(e) => {
                  const c = customer.data?.contacts.find((c) => c.id === e.target.value);
                  change({ contactId: e.target.value, attn: c?.name ?? "" });
                }}
                disabled={!h.customerId || h.omitCustomer}
              >
                <option value="">{t("quotationWorkspace.companyContact")}</option>
                {(customer.data?.contacts ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {contactOptionLabel(c, t)}
                  </option>
                ))}
                {h.contactId && !customer.data?.contacts.some((c) => c.id === h.contactId) && (
                  <option value={h.contactId}>{existing?.attn || t("quotationWorkspace.savedContact")}</option>
                )}
              </Select>
            </div>
            <div className={SPAN_CLASS[3]}>
              <FieldLabel htmlFor="quote-attn">{t("quotations.attn")}</FieldLabel>
              <Input id="quote-attn" value={h.attn} onChange={(e) => change({ attn: e.target.value })} />
            </div>
          </FieldGrid>
          {h.customerId && customer.isLoading && <p className="text-xs text-ink-soft">{t("common.loading")}</p>}
          {h.customerId && customer.error && (
            <p role="alert" className="text-sm text-danger">
              {t("quotationWorkspace.contactLoadError")}{" "}
              <Button variant="ghost" onClick={() => void customer.refetch()}>
                {t("common.retry")}
              </Button>
            </p>
          )}
          {(showRecipient || showRefresh) && (
            <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 rounded-sm border border-line bg-sunken/40 px-3 py-2.5">
              {showRecipient ? (
                <div className="min-w-0">
                  <p className="text-xs font-medium text-ink-soft">{t("quotationWorkspace.printedRecipient")}</p>
                  <p className="mt-1 whitespace-pre-line text-xs text-ink">{recipientLines.filter(Boolean).join("\n")}</p>
                </div>
              ) : <span />}
              {showRefresh && (
                <div className="max-w-sm sm:text-right">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!customer.data || customer.isFetching}
                    onClick={() => {
                      setRefreshRecipient(true);
                      change({ attn: customer.data?.contacts.find((c) => c.id === h.contactId)?.name ?? h.attn });
                    }}
                  >
                    {t("quotationWorkspace.refreshRecipient")}
                  </Button>
                  <p className="mt-1 text-xs text-ink-soft">
                    {t(refreshRecipient ? "quotationWorkspace.refreshPending" : "quotationWorkspace.savedRecipientHint")}
                  </p>
                </div>
              )}
            </div>
          )}
        </FormGroup>

        <FormGroup id="quote-group-terms" title={t("quotationWorkspace.groups.terms")} hint={t("quotationWorkspace.groups.termsHint")}>
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 @md:grid-cols-2 @3xl:grid-cols-5">
            <div>
              <FieldLabel htmlFor="quote-issueDate">{t("pi.date")}</FieldLabel>
              <Input id="quote-issueDate" type="date" value={h.issueDate} onChange={(e) => change({ issueDate: e.target.value })} />
              <Hint>{t("quotationWorkspace.required")}</Hint>
            </div>
            <div>
              <FieldLabel htmlFor="quote-printingDate">{t("quotations.printingDate")}</FieldLabel>
              <Input id="quote-printingDate" type="date" value={h.printingDate} onChange={(e) => change({ printingDate: e.target.value })} />
            </div>
            <div>
              <FieldLabel htmlFor="quote-validUntil">{t("quotationWorkspace.validUntil")}</FieldLabel>
              <Input id="quote-validUntil" type="date" value={h.validUntil} onChange={(e) => change({ validUntil: e.target.value })} />
            </div>
            <div>
              <FieldLabel htmlFor="quote-currency">{t("common.currencyLabel")}</FieldLabel>
              {/* Ô chọn thật, như PI: <datalist> trên Chrome chỉ gợi ý mục khớp chữ đang có trong ô, nên ô đang "USD" chỉ hiện
                  đúng USD (Roary báo 27/10/2026). Tiền tệ cũ không có trong danh sách vẫn được giữ làm một lựa chọn. */}
              <Select id="quote-currency" containerClassName="w-full" className="font-mono" value={h.currency} onChange={(e) => changeCurrency(e.target.value)}>
                {!currencies.includes(h.currency) && <option value={h.currency}>{h.currency}</option>}
                {currencies.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </Select>
              <Hint>{t("quotationWorkspace.required")}</Hint>
            </div>
            <div>
              <FieldLabel htmlFor="quote-preparer">{t("quotations.preparedBy")}</FieldLabel>
              <Input id="quote-preparer" list="quote-preparers" value={h.preparedBy} onChange={(e) => change({ preparedBy: e.target.value })} />
              <datalist id="quote-preparers">
                {(lists?.quotationPreparedBy ?? []).map((n) => (
                  <option key={n} value={n} />
                ))}
              </datalist>
            </div>
          </div>
        </FormGroup>

        {/* Hàng báo giá — bảng độ rộng cố định; cột phụ (MCQ, MSQ, giá DTD, ghi chú dòng) ẩn khi không dòng nào dùng. */}
        <FormGroup
          id="quote-group-lines"
          flush
          title={t("quotationWorkspace.groups.lines")}
          hint={t("quotationWorkspace.perUnitNotice", { currency: h.currency })}
          actions={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={sideUsed}
              title={sideUsed ? t("quotationWorkspace.sideLocked") : t("quotationWorkspace.sideColumns")}
              aria-pressed={showSide}
              onClick={() => setSideOpen((open) => !open)}
            >
              {showSide ? t("quotationWorkspace.sideHide") : t("quotationWorkspace.sideShow")}
            </Button>
          }
        >
          {currencyReview && (
            <div className="border-b border-line bg-warn-tint px-4 py-3">
              <p className="text-sm text-warn">{t("quotationWorkspace.currencyCleared")}</p>
              <label className="mt-2 flex items-center gap-2 text-sm">
                <input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} />
                {t("quotationWorkspace.pricesReviewed")}
              </label>
            </div>
          )}
          <QuotationLinesEditor
            lang={h.printLanguage}
            lines={lines}
            onChange={(value) => {
              setLines(value);
              setDirty(true);
              setError(null);
            }}
            currency={h.currency}
            units={lists?.productUnits ?? []}
            showSide={showSide}
          />
        </FormGroup>

        <FormGroup id="quote-group-notes" title={t("quotationWorkspace.groups.notes")}>
          <div className="grid grid-cols-1 gap-4 @2xl:grid-cols-2">
            {(["remarks", "validityTerms"] as const).map((key) => (
              <div key={key}>
                <FieldLabel htmlFor={`quote-${key}`}>
                  {t(key === "remarks" ? "common.remarks" : "quotationWorkspace.validityTerms")}
                </FieldLabel>
                <textarea
                  id={`quote-${key}`}
                  rows={4}
                  value={h[key]}
                  onChange={(e) => change({ [key]: e.target.value })}
                  className="w-full rounded-sm border border-line-strong bg-surface px-2.5 py-2 text-sm text-ink outline-none transition-colors focus:border-brand"
                />
              </div>
            ))}
          </div>
        </FormGroup>
      </fieldset>
      {error && (
        <p role="alert" className="rounded-sm bg-danger-tint px-3 py-2 text-sm text-danger">
          {error}
        </p>
      )}
      {/* Một thanh Hủy / Lưu duy nhất, dính đáy (19/09/2026 — bỏ cặp nút ở đầu trang). */}
      <div className="sticky bottom-0 z-20 flex flex-wrap items-center justify-end gap-x-3 gap-y-2 rounded-md border border-line bg-surface px-4 py-3 shadow-[var(--shadow-card)]">
        <p className="mr-auto text-xs text-ink-faint">{t("quotationWorkspace.editorHint")}</p>
        <Button variant="secondary" onClick={() => navigate(cancelTo)} disabled={busy}>
          {t("common.cancel")}
        </Button>
        <Button onClick={() => void save()} disabled={!canWrite || busy}>
          {t(busy ? "common.saving" : "common.save")}
        </Button>
      </div>
      <ConfirmDialog
        open={blocker.state === "blocked"}
        title={t("common.unsavedTitle")}
        description={t("common.unsavedDescription")}
        confirmLabel={t("common.discardChanges")}
        cancelLabel={t("common.keepEditing")}
        busy={busy}
        onCancel={() => blocker.state === "blocked" && blocker.reset()}
        onConfirm={() => blocker.state === "blocked" && blocker.proceed()}
      />
    </PageBody>
  );
}
